import React from 'react'
import { useTranslation } from 'react-i18next'

import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import LoadingButton from '@mui/lab/LoadingButton'

import { post } from '../../utils/service'
import Alert from '../alert/alert'
import { Log } from '../../types/log'
import NumberField from '../fields/numberField'
import { getFullscreenElement, getValueSuffix } from '../../utils/graphUtils'
import { SeriesType } from '../../types/enums/seriesType'
import { isPounds } from '../../utils/localStorageUtils'
import { convertToWeightScale, convertFromWeightScale, calculateWeightLoss } from '../../utils/weightUtils'
import UpdateInventoryAlert from './updateInventoryAlert'

interface BatchWeightDialogProps {
  log: Log
  maxBatchWeightInGrams: number
  onClose: () => void
  onSaveSuccess: (newLogData: Log) => void
}

const BatchWeightDialog = (props: BatchWeightDialogProps): JSX.Element => {
  const { t } = useTranslation()

  const [startWeight, setStartWeight] = React.useState<number>(
    convertToWeightScale(props.log.start_weight || props.log.profile_data?.batch_weight || 0)
  )
  const [endWeight, setEndWeight] = React.useState<number>(convertToWeightScale(props.log.end_weight || 0))
  const [isEndWeightMoreThanStartWeight, setIsEndWeightMoreThanStartWeight] = React.useState<boolean>(false)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const [hasSaveError, setHasSaveError] = React.useState<boolean>(false)

  const saveBatchWeight = (newStartWeight: number, newEndWeight: number): void => {
    if (newStartWeight > 0 && newEndWeight > newStartWeight) {
      setIsEndWeightMoreThanStartWeight(true)
      return
    }
    setIsEndWeightMoreThanStartWeight(false)
    const startWeightToSave = convertFromWeightScale(newStartWeight)
    const endWeightToSave = convertFromWeightScale(newEndWeight)
    setIsSaving(true)
    post<{ start_weight: number | null; end_weight: number | null }, Log>(`${props.log.url}weights/`, {
      start_weight: startWeightToSave || null,
      end_weight: endWeightToSave || null,
    })
      .then((response) => {
        props.onSaveSuccess(response.data)
      })
      .catch(() => {
        setHasSaveError(true)
        setIsSaving(false)
      })
  }

  return (
    <Dialog open={true} onClose={props.onClose} container={getFullscreenElement() || document.body}>
      <DialogContent>
        <Stack spacing={2}>
          <Stack sx={{ width: 125 }}>
            <NumberField
              isSmall
              autoFocus={true}
              valueSuffix={getValueSuffix(SeriesType.WEIGHT)}
              numValue={startWeight}
              label={t('logview.startweight')}
              minNumValue={0}
              maxNumValue={Math.round(convertToWeightScale(props.maxBatchWeightInGrams) * 100) / 100}
              decimalPlaces={isPounds() ? 2 : 1}
              onBlur={(newValue: number) => setStartWeight(newValue)}
              onEnter={(newValue: number) => saveBatchWeight(newValue, endWeight)}
            />
          </Stack>
          <Stack sx={{ width: 125 }}>
            <NumberField
              isSmall
              valueSuffix={getValueSuffix(SeriesType.WEIGHT)}
              numValue={endWeight}
              label={t('logview.endweight')}
              minNumValue={0}
              maxNumValue={Math.round(convertToWeightScale(props.maxBatchWeightInGrams) * 100) / 100}
              decimalPlaces={isPounds() ? 2 : 1}
              onBlur={(newValue: number) => setEndWeight(newValue)}
              onEnter={(newValue: number) => saveBatchWeight(startWeight, newValue)}
            />
          </Stack>
          <Stack>{`${t('logview.loss')}: ${
            !!startWeight && !!endWeight ? calculateWeightLoss(startWeight, endWeight) : 'N/A'
          }`}</Stack>
          {hasSaveError && <Alert severity="error">{t('logview.setweighterror')}</Alert>}
          {isEndWeightMoreThanStartWeight && <Alert severity="error">{t('logview.endweighterror')}</Alert>}
          <UpdateInventoryAlert
            oldLog={props.log}
            patchedLog={{ ...props.log, start_weight: convertFromWeightScale(startWeight) }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={isSaving} onClick={() => saveBatchWeight(startWeight, endWeight)}>
          {t('logview.setweight')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default BatchWeightDialog
